import { PieChart } from '@mui/x-charts/PieChart';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';

interface BreakdownItem {
  label: string;
  value: number;
  color: string;
}

interface Props {
  breakdown: BreakdownItem[];
}

export default function CostBreakdown({ breakdown }: Props) {
  const total = Math.round(
    breakdown.reduce((s, d) => s + d.value, 0) * 100,
  ) / 100;

  if (!breakdown.length) {
    return (
      <Box sx={styles.panel}>
        <Typography sx={styles.title}>Cost breakdown</Typography>
        <Typography sx={styles.sub}>No data for this period</Typography>
      </Box>
    );
  }

  const pieData = breakdown.map(d => ({
    id:    d.label,
    label: d.label,
    value: d.value,
    color: d.color,
  }));

  const pieSettings = {
    width: 200,
    height: 170,
    hideLegend: true,
  };

  return (
    <Box sx={styles.panel}>
      <Typography sx={styles.title}>Cost breakdown</Typography>
      <Typography sx={styles.sub}>This period · £{total} total</Typography>

      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <PieChart
          series={[{
            data:             pieData,
            innerRadius:      52,
            outerRadius:      80,
            paddingAngle:     2,
            cornerRadius:     3,
            highlightScope:   { fade: 'global', highlight: 'item' },
            valueFormatter:   (item) =>
              `£${item.value} (${total > 0 ? Math.round((item.value / total) * 100) : 0}%)`,
          }]}
          sx={{
            '& .MuiChartsTooltip-root': { fontSize: 12 },
          }}
          {...pieSettings}
        />
      </Box>

      {/* Custom legend */}
      <Stack direction="row" spacing={1.5} sx={{ mt: 1, flexWrap: 'wrap' }}
        role="list" aria-label="Cost breakdown legend">
        {breakdown.map(item => (
          <Stack key={item.label} direction="row" alignItems="center"
            spacing={0.6} role="listitem">
            <Box sx={{
              width: 8, height: 8, borderRadius: '2px',
              flexShrink: 0, background: item.color,
            }} />
            <Typography sx={styles.legendLabel}>
              {item.label} £{item.value}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

const styles = {
  panel: {
    bgcolor:      'var(--color-bg-secondary)',
    borderRadius: 3,
    p:            '1.1rem 1.25rem',
  },
  title: {
    fontSize:   13,
    fontWeight: 600,
    color:      'var(--color-text-primary)',
    mb:         '2px',
  },
  sub: {
    fontFamily: "'DM Mono', monospace",
    fontSize:   11,
    color:      'var(--color-text-secondary)',
    mb:         '0.75rem',
  },
  legendLabel: {
    fontFamily: "'DM Mono', monospace",
    fontSize:   11,
    color:      'var(--color-text-secondary)',
  },
} as const;
